import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { intakeQuestionnaireActions } from '../../../../../../../store';

import type { AppDispatch, RootState } from '../../../../../../../store';
import type { IntakeMedicalDiagnosisPayload } from '../../../../../../../store/questionnaires/intake';

export default function useIntakeMedicalDiagnoses() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { intakeQuestionnaireId } = useParams<'intakeQuestionnaireId'>();
  const diagnoses = useSelector(
    (state: RootState) =>
      state.intakeQuestionnaire.intakeQuestionnaireDetails?.medical_info?.diagnoses ?? [],
  );
  const isSaving = useSelector(
    (state: RootState) =>
      state.intakeQuestionnaire.createMedicalDiagnosisLoading ||
      state.intakeQuestionnaire.updateMedicalDiagnosisLoading ||
      state.intakeQuestionnaire.deleteMedicalDiagnosisLoading,
  );

  const create = useCallback(
    async (data: IntakeMedicalDiagnosisPayload) => {
      if (!intakeQuestionnaireId) return undefined;
      const result = await dispatch(
        intakeQuestionnaireActions.createIntakeMedicalDiagnosis({
          intakeQuestionnaireId,
          data,
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.createIntakeMedicalDiagnosis.fulfilled.match(result)
        ? result.payload.id
        : undefined;
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  const update = useCallback(
    async (diagnosisId: number, data: IntakeMedicalDiagnosisPayload) => {
      if (!intakeQuestionnaireId) return false;
      const result = await dispatch(
        intakeQuestionnaireActions.updateIntakeMedicalDiagnosis({
          intakeQuestionnaireId,
          diagnosisId,
          data,
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.updateIntakeMedicalDiagnosis.fulfilled.match(result);
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  const remove = useCallback(
    async (diagnosisId: number) => {
      if (!intakeQuestionnaireId) return false;
      const result = await dispatch(
        intakeQuestionnaireActions.deleteIntakeMedicalDiagnosis({
          intakeQuestionnaireId,
          diagnosisId,
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.deleteIntakeMedicalDiagnosis.fulfilled.match(result);
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  return { diagnoses, isSaving, create, update, remove };
}
